/*
 * LISTS: the words the screens print, kept out of the components so the tests
 * can pin them (test/lists-model.test.js). Drawn in Claude Design
 * (Lists.dc canvas, 2026-10-15): the index, one list, the bulk bar, the undo
 * toast, the No Contact confirms, the person panel, the record rail and the
 * review queue.
 */

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "Aug 14", or "Aug 14, 2025" when it is not this year. */
export function shortDate(iso, now = new Date()) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const base = `${MONTHS[d.getMonth()]} ${d.getDate()}`;
  return d.getFullYear() === now.getFullYear() ? base : `${base}, ${d.getFullYear()}`;
}

/** "Aug 2026" */
export function monthYear(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : `${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

/** 1495 → "1,495" */
export function num(n) {
  return Number(n || 0).toLocaleString("en-US");
}

/** "Gabriel" for his own address, the address otherwise, "rule" as the rule. */
export function actorWord(by) {
  if (!by) return "";
  if (by === "rule") return "by rule";
  if (/^gabriel@/i.test(by) || by === "password login") return "by you";
  return `by ${by}`;
}

/** How a member got on: { word: "Rule" | "Added by you" | "Automation", date }. */
export function howOn(member, now = new Date()) {
  let word = "Added by you";
  if (member.source === "rule") word = "Rule";
  else if (member.source === "automation") word = "Automation";
  else if (actorWord(member.changed_by) && actorWord(member.changed_by) !== "by you") word = `Added ${actorWord(member.changed_by)}`;
  return { word, date: shortDate(member.added_at || member.changed_at, now) };
}

/**
 * The company cell. When their newest address has no company (a gmail) but an
 * older role does, the row says so instead of pretending the gmail is a job.
 */
export function companyCell(m) {
  const noCompanyOnEmail = m.email_contact_id && m.role_contact_id && m.email_contact_id !== m.role_contact_id && !m.email_company_id;
  if (noCompanyOnEmail && m.company) return { kind: "was", company: m.company, title: null };
  if (m.company) return { kind: "current", company: m.company, title: m.job_title || null };
  return { kind: "none", company: null, title: m.job_title || null };
}

/** The display name, never empty. */
export function personName(p) {
  const n = (p.name || [p.first_name, p.last_name].filter(Boolean).join(" ")).trim();
  return n || p.email || "No name on record";
}

/**
 * Roles for the person panel, newest first, each with its span. The span is
 * "since Aug 2026" for the current one and "first seen Mar 2025" for the rest:
 * a row's date is when we FIRST saw that role, so an end date would be a guess.
 */
export function roleRows(person) {
  const contacts = [...(person.contacts || [])].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  return contacts.map((c) => {
    const current = c.id === person.email_contact_id || c.id === person.role_contact_id;
    return {
      ...c,
      current,
      span: current ? `since ${monthYear(c.created_at)}` : `first seen ${monthYear(c.created_at)}`,
      join: joinWord(c.person_link),
    };
  });
}

/** Why this record is under this person, in words; null for the first/only one. */
export function joinWord(link) {
  if (link === "name") return "joined by name";
  if (link === "linkedin") return "same LinkedIn";
  if (link === "email") return "same email";
  if (link === "manual") return "linked by you";
  return null;
}

/** "3 · Firefly → Nebari → ORDR", oldest first, companies only, no repeats in a row. */
export function rolesTrail(person) {
  const contacts = [...(person.contacts || [])].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  const names = [];
  for (const c of contacts) if (c.company && names[names.length - 1] !== c.company) names.push(c.company);
  return names.length ? `${contacts.length} · ${names.join(" → ")}` : String(contacts.length);
}

/** The person's No Contact membership, if they are on it. */
export function noContactOf(person) {
  return (person.lists || []).find((l) => l.kind === "suppression" && l.state === "member") || null;
}

/** Lists they are on (ordinary ones), for the chips. */
export function listsOn(person) {
  return (person.lists || []).filter((l) => l.kind !== "suppression" && l.state === "member");
}

/** Where taking them off No Contact would put them back. */
export function wouldReturnTo(person) {
  return (person.lists || []).filter((l) => l.kind !== "suppression" && l.state === "removed" && l.reason === "no_contact");
}

/** "Also off Newsletter (4) and Q4 webinar invites (2)", or null. */
export function alsoOffLine(removedFrom) {
  const parts = (removedFrom || []).map((r) => `${r.name} (${r.count})`);
  if (!parts.length) return null;
  const joined = parts.length === 1 ? parts[0] : `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`;
  return `Also off ${joined}`;
}

/** The two No Contact confirms' words (frame 4). */
export function conflictCopy({ conflicts, total, listName }) {
  const n = conflicts.length;
  if (total === 1 && n === 1) {
    const c = conflicts[0];
    const first = (c.name || "").split(" ")[0] || "them";
    return {
      single: true,
      title: `${c.name || c.email || "This person"} is on No Contact.`,
      sub: `Take ${first} off No Contact and add to ${listName}?`,
      keep: "Keep on No Contact",
      go: "Take off and add",
      first,
    };
  }
  const rest = total - n;
  return {
    single: false,
    title: `${n} of these ${total} ${n === 1 ? "is" : "are"} on No Contact.`,
    sub: `Adding ${total} to ${listName}. ${n === 1 ? "This one is" : n === 2 ? "These two are" : n === 3 ? "These three are" : `These ${n} are`} blocked:`,
    restLabel: rest > 0 ? `Add the other ${rest}` : null,
    restNote: n === 1
      ? `${conflicts[0].name || "They"} stays on No Contact and off every list.`
      : `The ${n} stay on No Contact and stay off every list.`,
    allLabel: `Take ${n === 1 ? "them" : `all ${n}`} off No Contact and add all ${total}`,
    goRest: `Add ${rest} to ${listName}`,
    goAll: `Add ${total} to ${listName}`,
    rest,
  };
}

/** A check line: "✓ was a lead" / "✗ not staff — remotion.io is our own domain". */
export function checkLine(c) {
  return c.ok ? `✓ ${c.label}` : `✗ ${c.label}${c.fail ? ` — ${c.fail}` : ""}`;
}

/** A history row's words. */
export function historyLine(e) {
  const list = e.list_name || "a deleted list";
  const by = actorWord(e.actor);
  if (e.action === "blocked") return { list, text: [`Kept off`, list, `· on No Contact`] };
  if (e.action === "added") {
    if (e.reason === "off_no_contact") return { list, text: ["Back on", list, "· off No Contact"] };
    if (e.reason === "undo") return { list, text: ["Back on", list, "· undo"] };
    return { list, text: [e.source === "rule" ? "On" : "Added to", list, e.source === "rule" ? "by rule" : by] };
  }
  if (e.reason === "no_contact") return { list, text: ["Off", list, "· No Contact"] };
  if (e.reason === "no_longer_matches") return { list, text: ["Off", list, "· stopped matching the rule"] };
  return { list, text: ["Removed from", list, by] };
}

/** For the review queue: which record "Not the same" splits out. */
export function splitCandidate(item) {
  const cs = [...(item.contacts || [])].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  const current = new Set([item.email_contact_id, item.role_contact_id]);
  return cs.find((c) => c.link === "name" && !current.has(c.id)) || cs.find((c) => !current.has(c.id)) || null;
}

/** Sort keys the list page offers, in the order the footer prints them. */
export const SORTS = [
  { key: "name", label: "Name" },
  { key: "company", label: "Current company" },
  { key: "added", label: "Date on list" },
  { key: "roles", label: "Roles" },
];
